import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { estudianteService } from "@/services/api";

const TIPOS_INFORME = [
  { value: "integral", label: "Informe Integral del Estudiante" },
  { value: "rendimiento", label: "Informe de Rendimiento Académico" },
  { value: "comportamiento", label: "Informe de Comportamiento" },
  { value: "asistencia", label: "Informe de Asistencia" },
  { value: "convivencia", label: "Informe de Convivencia Escolar" },
  { value: "orientacion", label: "Informe de Orientación" },
  { value: "compromiso_apoderado", label: "Informe de Compromiso del Apoderado" },
  { value: "psicoeducativo", label: "Informe Psicoeducativo" },
];

export default function GenerarInformePage() {
  const navigate = useNavigate();
  const [estudianteId, setEstudianteId] = useState("");
  const [tipoInforme, setTipoInforme] = useState("integral");
  const [tribunal, setTribunal] = useState("");
  const [causa, setCausa] = useState("");
  const [generando, setGenerando] = useState(false);
  const [generado, setGenerado] = useState(false);

  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadStudents() {
      try {
        setLoading(true);
        setError("");
        const data = await estudianteService.list();
        if (Array.isArray(data)) {
          setStudents(data);
        }
      } catch (err: any) {
        console.error("Error loading students for report wizard:", err);
        setError("Error al cargar estudiantes: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadStudents();
  }, []);

  const estudiante = students.find((s) => s.id === estudianteId);

  const handleGenerar = () => {
    setGenerando(true);
    setTimeout(() => {
      setGenerando(false);
      setGenerado(true);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-2" />
        <p className="text-sm text-foreground-500">Cargando datos del asistente...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in-up max-w-3xl">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Generar Informe con IA</h1>
        <p className="text-sm text-foreground-500 mt-0.5">La IA usará solo la información registrada en la plataforma para generar el borrador</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {generado ? (
        <div className="bg-white border border-background-200/80 rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4">
            <i className="ri-check-double-line text-lg" />
            <div>
              <p className="text-sm font-semibold">Borrador generado exitosamente</p>
              <p className="text-xs text-green-600">Revisa, edita y valida antes de enviar</p>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-5 max-h-96 overflow-y-auto border border-background-200">
            <pre className="text-xs text-foreground-700 font-sans whitespace-pre-wrap leading-relaxed">{`INFORME ESCOLAR — ${TIPOS_INFORME.find((t) => t.value === tipoInforme)?.label.toUpperCase()}

I. IDENTIFICACIÓN
Nombre: ${estudiante?.nombre || "[Seleccionar estudiante]"}
Curso: ${estudiante?.curso || "—"}
Profesor Jefe: ${estudiante?.profesorJefe || estudiante?.profesor_jefe || "—"}
Establecimiento: Liceo Valentina Tereshkova

II. ANTECEDENTES RECOPILADOS
[La IA ha recopilado antecedentes desde los registros del caso,
intervenciones, documentos e información proporcionada por docentes]

III. RENDIMIENTO ACADÉMICO
[Información proporcionada por profesor jefe — pendiente de completar]

IV. ASISTENCIA
[Registro de asistencia del período correspondiente]

V. COMPORTAMIENTO Y CONVIVENCIA ESCOLAR
[Observaciones del libro de clases y registros de convivencia]

VI. INTERVENCIONES REALIZADAS
[Resumen de intervenciones registradas en la plataforma]

VII. CONCLUSIÓN
[Resumen objetivo basado en antecedentes registrados]

---
*Borrador generado por Ezer Convivencia360.*
*Requiere validación profesional antes de su envío.*
*Este informe se basa exclusivamente en información registrada en la plataforma.*
*No contiene diagnósticos clínicos ni juicios personales.*`}</pre>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate("/orientacion/informes")} className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap">Editar Borrador</button>
            <button className="px-4 py-2 border border-background-200 rounded-xl text-sm cursor-pointer whitespace-nowrap">Descargar Word</button>
            <button className="px-4 py-2 border border-background-200 rounded-xl text-sm cursor-pointer whitespace-nowrap">Descargar PDF</button>
            <button onClick={() => setGenerado(false)} className="px-4 py-2 text-sm text-foreground-500 cursor-pointer whitespace-nowrap">Generar otro</button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-background-200/80 rounded-2xl p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
            <strong>Importante:</strong> El informe se genera como borrador y debe ser revisado por un profesional. La IA no inventa antecedentes ni emite diagnósticos clínicos.
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Estudiante</label>
            <select
              value={estudianteId}
              onChange={(e) => setEstudianteId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              <option value="">Seleccionar estudiante</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre} · {s.curso}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground-700 mb-2">Tipo de Informe</label>
            <select
              value={tipoInforme}
              onChange={(e) => setTipoInforme(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {TIPOS_INFORME.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">Tribunal solicitante (opcional)</label>
              <input
                type="text"
                value={tribunal}
                onChange={(e) => setTribunal(e.target.value)}
                placeholder="Ej: Tribunal de Familia de Santiago"
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">N° Causa (opcional)</label>
              <input
                type="text"
                value={causa}
                onChange={(e) => setCausa(e.target.value)}
                placeholder="Ej: RIT C-2345-2026"
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleGenerar}
              disabled={!estudianteId || generando}
              className="px-6 py-2.5 bg-accent-500 text-white rounded-xl text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {generando ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  Generando borrador...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <i className="ri-magic-line" />
                  Generar Borrador con IA
                </span>
              )}
            </button>
            <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-sm font-medium text-foreground-500 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
          </div>

          {generando && (
            <div className="mt-4">
              <p className="text-xs text-foreground-400 mb-2">La IA está recopilando antecedentes del caso...</p>
              <div className="h-2 bg-background-100 rounded-full overflow-hidden">
                <div className="h-full bg-accent-500 rounded-full animate-pulse" style={{ width: "60%" }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}